import { DateTime } from 'luxon';
import { BookingCompressed } from '@jooxter/api';
import { fromISO, IRange } from '@jooxter/utils';
import { IOverlapPartitionResult, OverlapResolutionActionEnum } from './types';

// One-sided truncation: keep whichever portion of `existing` falls outside `incoming`, discard the rest.
// Returns undefined when `existing` is fully contained within `incoming` (no valid remainder on either side),
// or when the remainder's end would already be in the past (e.g. `existing` has already started and
// `incoming` starts now-ish) - the backend rejects an update whose end is in the past, and there's no
// grace period to reuse there like there is for a booking's start.
export const computeTruncatedRange = (
  existing: IRange,
  incoming: IRange,
  now: DateTime = DateTime.now()
): IRange | undefined => {
  let range: IRange | undefined;

  if (existing.from < incoming.from) {
    range = { from: existing.from, to: incoming.from };
  } else if (existing.to > incoming.to) {
    range = { from: incoming.to, to: existing.to };
  }

  return range && range.to > now ? range : undefined;
};

// Whether the principal is allowed to cancel/delete this specific booking - a per-booking backend
// flag (e.g. locked, already past a cancellation cutoff), not just a matter of being the organizer.
export const canDeleteBooking = (booking: BookingCompressed): boolean => !!booking.capabilities?.cancel;

// Whether the principal is allowed to update this specific booking - required for both Truncate
// (a partial update) and Move (a full update once the pivot form is auto-submitted).
export const canUpdateBooking = (booking: BookingCompressed): boolean => !!booking.capabilities?.update;

// Picks the first genuinely viable choice for a booking - Delete, then Truncate, then Move - so that
// when only one choice ends up available (e.g. capabilities rule out Delete and the time-based guard
// rules out Truncate), that one is what's pre-selected, rather than always defaulting to Delete or to
// whichever of Truncate/Delete happens to be capability-allowed regardless of its actual availability.
export const defaultActionFor = (
  booking: BookingCompressed,
  newRange: IRange,
  now: DateTime,
  allowMove: boolean
): OverlapResolutionActionEnum => {
  if (canDeleteBooking(booking)) {
    return OverlapResolutionActionEnum.Delete;
  }

  if (canUpdateBooking(booking)) {
    const existingRange: IRange = {
      from: fromISO(booking.start.dateTime, booking.resource?.timezone),
      to: fromISO(booking.end.dateTime, booking.resource?.timezone),
    };

    if (computeTruncatedRange(existingRange, newRange, now)) {
      return OverlapResolutionActionEnum.Truncate;
    }

    if (allowMove) {
      return OverlapResolutionActionEnum.Move;
    }
  }

  // No choice is actually viable - Confirm is disabled in this case (see `hasUnresolvableBooking` in
  // useOverlapResolution), so this fallback value is never actually submitted.
  return OverlapResolutionActionEnum.Delete;
};

export const partitionOverlapActions = (
  bookings: BookingCompressed[],
  actions: Map<number, OverlapResolutionActionEnum>,
  newRange: IRange,
  now: DateTime = DateTime.now()
): IOverlapPartitionResult => {
  const result: IOverlapPartitionResult = { toDelete: [], toTruncate: [] };

  bookings.forEach((booking) => {
    const action = actions.get(booking.id) ?? OverlapResolutionActionEnum.Delete;

    if (action === OverlapResolutionActionEnum.Move && canUpdateBooking(booking)) {
      result.toMove = booking;
      return;
    }

    if (action === OverlapResolutionActionEnum.Truncate && canUpdateBooking(booking)) {
      const existingRange: IRange = {
        from: fromISO(booking.start.dateTime, booking.resource?.timezone),
        to: fromISO(booking.end.dateTime, booking.resource?.timezone),
      };
      const truncatedRange = computeTruncatedRange(existingRange, newRange, now);

      if (truncatedRange) {
        result.toTruncate.push({ booking, range: truncatedRange });
        return;
      }
    }

    // Falls back to Delete whenever the selected (or defaulted) action isn't actually permitted or
    // viable - unless Delete itself isn't permitted either, in which case this booking is left out of
    // every bucket. That dead end is only reachable if the Confirm button is clicked while disabled
    // (see `hasUnresolvableBooking` in useOverlapResolution, which disables it precisely for this case).
    if (canDeleteBooking(booking)) {
      result.toDelete.push(booking.id);
    }
  });

  return result;
};
